/**
 * Contexto de atribuição do CTA de teste grátis.
 *
 * Guarda em `sessionStorage` (NUNCA `localStorage`) de onde o visitante clicou,
 * para que o `generate_lead` disparado no cadastro saiba a qual página,
 * segmento e posição de CTA o lead pertence.
 *
 * Só valores de enum entram aqui. Toda operação de storage é defensiva:
 * navegador em modo privado, storage cheio, cookie bloqueado ou JSON corrompido
 * nunca podem interromper o clique nem o cadastro.
 */

import {
  BUSINESS_SEGMENT_VALUES,
  CTA_CONTEXT_STORAGE_KEY,
  CTA_CONTEXT_TTL_MS,
  CTA_PAGE_VALUES,
  CTA_PLACEMENT_VALUES,
  DESTINATION_VALUES,
  PLAN_TYPE_VALUES,
  PRODUCT_VALUES,
} from "./constants";

/** Chaves aceitas no objeto persistido — allowlist estrita. */
const ALLOWED_KEYS = Object.freeze([
  "cta_page",
  "cta_placement",
  "business_segment",
  "destination",
  "plan_type",
  "product",
  "timestamp",
]);

/**
 * Chaves obrigatórias. `plan_type` e `product` são opcionais.
 *
 * ⚠️ `product` é opcional PORQUE a ausência é informação: quem clica em
 * `/recursos` não escolheu produto nenhum. Torná-lo obrigatório invalidaria o
 * contexto daquela página — e a invalidação aqui é SILENCIOSA (`saveCtaContext`
 * devolve `false` e ninguém checa), então o sintoma seria um `generate_lead`
 * sem atribuição, não um erro.
 */
const REQUIRED_KEYS = Object.freeze([
  "cta_page",
  "cta_placement",
  "business_segment",
  "destination",
  "timestamp",
]);

/** Enum esperado por chave. */
const ENUM_BY_KEY = Object.freeze({
  cta_page: CTA_PAGE_VALUES,
  cta_placement: CTA_PLACEMENT_VALUES,
  business_segment: BUSINESS_SEGMENT_VALUES,
  destination: DESTINATION_VALUES,
  plan_type: PLAN_TYPE_VALUES,
  product: PRODUCT_VALUES,
});

/**
 * Acesso ao sessionStorage protegido — retorna null se indisponível.
 * @returns {Storage|null}
 */
function getStorage() {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage || null;
  } catch (_err) {
    return null;
  }
}

/** @returns {boolean} true se `value` for objeto simples (não array/null). */
function isPlainObject(value) {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === "[object Object]"
  );
}

/**
 * Valida o objeto lido do storage. Qualquer desvio invalida tudo — não há
 * conserto parcial, porque contexto meio válido gera métrica errada.
 *
 * @param {unknown} raw
 * @returns {object|null} objeto validado ou null.
 */
function validateContext(raw) {
  if (!isPlainObject(raw)) return null;

  const keys = Object.keys(raw);

  // Allowlist estrita: chave desconhecida invalida o objeto inteiro.
  if (keys.some((key) => !ALLOWED_KEYS.includes(key))) return null;
  if (REQUIRED_KEYS.some((key) => !keys.includes(key))) return null;

  if (typeof raw.timestamp !== "number" || !Number.isFinite(raw.timestamp)) {
    return null;
  }

  const validEnums = keys
    .filter((key) => key !== "timestamp")
    .every((key) => ENUM_BY_KEY[key].includes(raw[key]));
  if (!validEnums) return null;

  const context = {
    cta_page: raw.cta_page,
    cta_placement: raw.cta_placement,
    business_segment: raw.business_segment,
    destination: raw.destination,
    timestamp: raw.timestamp,
  };
  if (keys.includes("plan_type")) context.plan_type = raw.plan_type;
  if (keys.includes("product")) context.product = raw.product;

  return context;
}

/**
 * Grava o contexto do clique de CTA.
 *
 * @param {{cta_page: string, cta_placement: string, business_segment: string,
 *          destination: string, plan_type?: string, product?: string}} ctx
 * @returns {boolean} true se gravou.
 */
export function saveCtaContext(ctx) {
  try {
    const storage = getStorage();
    if (!storage) return false;

    const candidate = { ...ctx, timestamp: Date.now() };
    const validated = validateContext(candidate);
    if (!validated) return false;

    storage.setItem(CTA_CONTEXT_STORAGE_KEY, JSON.stringify(validated));
    return true;
  } catch (_err) {
    return false;
  }
}

/**
 * Lê e valida o contexto. Contexto expirado (>30 min) é descartado E removido
 * do storage, para não sujar um cadastro futuro.
 *
 * @returns {object|null}
 */
export function readCtaContext() {
  try {
    const storage = getStorage();
    if (!storage) return null;

    const raw = storage.getItem(CTA_CONTEXT_STORAGE_KEY);
    if (!raw) return null;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (_parseErr) {
      clearCtaContext();
      return null;
    }

    const validated = validateContext(parsed);
    if (!validated) {
      clearCtaContext();
      return null;
    }

    const age = Date.now() - validated.timestamp;
    if (age < 0 || age > CTA_CONTEXT_TTL_MS) {
      clearCtaContext();
      return null;
    }

    return validated;
  } catch (_err) {
    return null;
  }
}

/** Remove o contexto do storage. Silencioso em qualquer falha. */
export function clearCtaContext() {
  try {
    const storage = getStorage();
    if (!storage) return;
    storage.removeItem(CTA_CONTEXT_STORAGE_KEY);
  } catch (_err) {
    // silencioso
  }
}
